import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { playersAPI, evaluationsAPI, trialPeriodsAPI } from '../services/api';
import './EvaluationHistory.css';

interface Player {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface EvaluationRecord {
  id: string;
  evaluationDate: string;
  createdAt: string;
  season: string;
  data: Record<string, any>;
  category: Category;
  player: Player;
  trialPeriod?: {
    id: string;
    name: string;
    season: string;
    year: number;
  };
}

interface TrialPeriod {
  id: string;
  name: string;
  season: string;
  year: number;
  quarter: number;
}

const EvaluationHistory: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [players, setPlayers] = useState<Player[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [trialPeriods, setTrialPeriods] = useState<TrialPeriod[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [history, setHistory] = useState<EvaluationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if user is a player/guardian (not admin)
  const isPlayerOrGuardian = (user?.role === 'player' || user?.role === 'guardian') && !isAdmin;

  useEffect(() => {
    loadInitialData();
  }, []);

  // Auto-select logged-in player for players/guardians
  useEffect(() => {
    if (isPlayerOrGuardian && user?.player?.id && !selectedPlayer) {
      setSelectedPlayer(user.player.id);
    }
  }, [isPlayerOrGuardian, user, selectedPlayer]);

  useEffect(() => {
    if (selectedPlayer) {
      loadPlayerHistory();
    } else {
      setHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayer]);

  const loadInitialData = async () => {
    try {
      const [playersRes, categoriesRes, periodsRes] = await Promise.all([
        playersAPI.getAll(),
        evaluationsAPI.getCategories(),
        trialPeriodsAPI.getAll(),
      ]);
      
      const sortedPlayers = playersRes.data.sort((a: Player, b: Player) => 
        `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
      );
      
      setPlayers(sortedPlayers);
      setCategories(categoriesRes.data);
      setTrialPeriods(periodsRes.data);
    } catch (err: any) {
      console.error('Failed to load initial data:', err);
      setError('Failed to load data');
    }
  };

  const loadPlayerHistory = async () => {
    if (!selectedPlayer) return;
    
    try {
      setLoading(true);
      const response = await evaluationsAPI.getPlayerEvaluationHistory(selectedPlayer);
      setHistory(response.data);
      setError('');
    } catch (err: any) {
      console.error('Failed to load history:', err);
      setError('Failed to load evaluation history');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  let filteredHistory = history;
  
  if (selectedCategory) {
    filteredHistory = filteredHistory.filter(record => record.category.id === selectedCategory);
  }
  
  if (selectedPeriod) {
    filteredHistory = filteredHistory.filter(record => record.trialPeriod?.id === selectedPeriod);
  }

  const groupedByCategory = filteredHistory.reduce((acc, record) => {
    const categoryName = record.category.name;
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(record);
    return acc;
  }, {} as Record<string, EvaluationRecord[]>);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMetricChange = (records: EvaluationRecord[], metricKey: string) => {
    if (records.length < 2) return null;
    
    const latest = records[0].data[metricKey];
    const previous = records[1].data[metricKey];
    
    if (!latest || !previous) return null;
    
    const change = parseFloat(latest) - parseFloat(previous);
    const isImprovement = change > 0; // This is simplified - you'd need to know if higher is better
    
    return { change, isImprovement };
  };

  const selectedPlayerData = players.find(p => p.id === selectedPlayer);

  return (
    <div className="evaluation-history">
      <div className="page-header">
        <h1>📊 Evaluation History & Progress Tracking</h1>
        <p className="subtitle">Track player performance over time and view historical test results</p>
      </div>

      <div className="filters-section">
        {!isPlayerOrGuardian && (
          <div className="filter-group">
            <label htmlFor="player-select">Select Player:</label>
            <select
              id="player-select"
              value={selectedPlayer}
              onChange={(e) => {
                setSelectedPlayer(e.target.value);
                setSelectedCategory('');
              }}
              className="filter-select"
            >
              <option value="">-- Choose a Player --</option>
              {players.map(player => (
                <option key={player.id} value={player.id}>
                  {player.lastName}, {player.firstName}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedPlayer && (
          <>
            <div className="filter-group">
              <label htmlFor="category-select">Filter by Category:</label>
              <select
                id="category-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="">-- All Categories --</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="period-select">Filter by Trial Period:</label>
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="filter-select"
              >
                <option value="">-- All Periods --</option>
                {trialPeriods.map(period => (
                  <option key={period.id} value={period.id}>
                    {period.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading && <div className="loading">Loading history...</div>}

      {!loading && selectedPlayer && selectedPlayerData && (
        <div className="history-content">
          <div className="player-info-banner">
            <h2>{selectedPlayerData.firstName} {selectedPlayerData.lastName}</h2>
            <p className="total-evaluations">
              Total Evaluations: <strong>{filteredHistory.length}</strong>
              {selectedCategory && ` (${categories.find(c => c.id === selectedCategory)?.name})`}
              {selectedPeriod && ` - ${trialPeriods.find(p => p.id === selectedPeriod)?.name}`}
            </p>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="no-data">
              <p>No evaluation history found for this player
                {selectedCategory ? ' in this category' : ''}
                {selectedPeriod ? ' in this period' : ''}.</p>
            </div>
          ) : (
            <div className="history-grid">
              {Object.entries(groupedByCategory).map(([categoryName, records]) => (
                <div key={categoryName} className="category-history-section">
                  <h3 className="category-title">{categoryName}</h3>
                  <p className="records-count">{records.length} evaluation{records.length !== 1 ? 's' : ''}</p>
                  
                  <div className="timeline">
                    {records.map((record, index) => (
                      <div key={record.id} className="timeline-item">
                        <div className="timeline-marker">
                          <span className="timeline-number">{index + 1}</span>
                        </div>
                         <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="timeline-date">{formatDate(record.evaluationDate)}</span>
                            <span className="timeline-season">{record.trialPeriod?.name || record.season}</span>
                            {index === 0 && <span className="badge-latest">Most Recent</span>}
                          </div>
                          
                          <div className="metrics-grid">
                            {Object.entries(record.data).map(([key, value]) => {
                              if (key.includes('Rank')) return null;
                              
                              const change = index < records.length - 1 
                                ? getMetricChange([record, records[index + 1]], key)
                                : null;
                              
                              return (
                                <div key={key} className="metric-item">
                                  <span className="metric-label">{key}:</span>
                                  <span className="metric-value">{value}</span>
                                  {change && (
                                    <span className={`metric-change ${change.isImprovement ? 'improved' : 'declined'}`}>
                                      {change.change > 0 ? '+' : ''}{change.change.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!selectedPlayer && !loading && (
        <div className="empty-state">
          <div className="empty-icon">📈</div>
          <h3>Select a Player to View History</h3>
          <p>Choose a player from the dropdown above to view their evaluation history and track their progress over time.</p>
        </div>
      )}
    </div>
  );
};

export default EvaluationHistory;
